import type {
  ApplyAcls,
  CreateVersionCopyableSegment,
  CreateVersionParams,
} from '@iconik-io/iconik-api/src/sdk/assets';
import type { FilesProxyCreateSchema } from '@iconik-io/iconik-api/src/services/files';
import type {
  FileInfo,
  UploaderState,
  UploaderTarget,
  UploadKind,
  UploadOptions,
} from '@iconik-io/uploader/src/state';

import type { NewSubtitleMetadata } from '../../../components/modals/new_subtitle/types';
import { trimPath } from '../helpers';
import type {
  PickedFile,
  UploaderCollectionPermissions,
  UploaderTopLevelPermissions,
  UploadItem,
} from '../types';

export type UploadRequest = {
  fileInfo: FileInfo;
  options: UploadOptions;
  target: UploaderTarget;
  uploadKind: UploadKind;
};

type CommonUploadContext = {
  storageId?: string;
  transcodePriority: number;
};

const toFileInfo = (file: PickedFile, targetPath: string): FileInfo => ({
  targetPath,
  size: file.size,
  mimeType: file.type,
  streamSource: { type: 'fileRef', id: file.fileRefId },
});

const toCommonOptions = (context: CommonUploadContext): UploadOptions => ({
  storageId: context.storageId,
  transcodePriority: context.transcodePriority,
});

/**
 * The package builds the collection tree from the directory part of the target
 * path, so dropping the directories is all it takes not to reflect them.
 */
export const getTargetPath = (
  name: string,
  fullpath: string | undefined,
  reflectDirectories: boolean,
): string => {
  if (!reflectDirectories || !fullpath) {
    return name;
  }
  return trimPath(fullpath) || name;
};

/**
 * A new asset queued as the file is added to the list. What the panel settings
 * decide is only known once the upload starts, see getNewAssetStartUpdate.
 */
export const toNewAssetUpload = (
  file: PickedFile,
  context: CommonUploadContext & {
    collectionId?: string;
    reflectDirectories: boolean;
  },
): UploadRequest => ({
  fileInfo: toFileInfo(
    file,
    getTargetPath(file.name, file.fullpath, context.reflectDirectories),
  ),
  options: {
    ...toCommonOptions(context),
    baseCollectionId: context.collectionId,
  },
  target: { type: 'newAsset' },
  uploadKind: { kind: 'file' },
});

type NewAssetStartContext = CommonUploadContext & {
  reflectDirectories: boolean;
  collectionPermissions: UploaderCollectionPermissions;
  topLevelPermissions: UploaderTopLevelPermissions;
  isShare: boolean;
};

/**
 * Collections created under the destination take the collection permissions,
 * and so does the asset landing in one. At the root only the top level
 * permissions apply.
 */
const getNewAssetAcls = (
  collectionId: string | undefined,
  context: NewAssetStartContext,
): Pick<UploadOptions, 'assetACL' | 'collectionACL'> => {
  const permissions = collectionId
    ? context.collectionPermissions
    : context.topLevelPermissions;
  // A share only lets the asset be assigned to the shared collection, which
  // the package does whenever the asset has one.
  const assetACL: ApplyAcls = context.isShare ? 'unspecified' : permissions;
  return { assetACL, collectionACL: permissions };
};

/** The panel settings a staged new asset upload starts with. */
export const getNewAssetStartUpdate = (
  state: UploaderState,
  item: UploadItem | undefined,
  context: NewAssetStartContext,
): { options: Partial<UploadOptions>; targetPath: string } => {
  const name = state.fileInfo.targetPath.split('/').pop() ?? '';
  return {
    options: {
      ...toCommonOptions(context),
      ...getNewAssetAcls(state.options.baseCollectionId, context),
    },
    targetPath: getTargetPath(name, item?.fullpath, context.reflectDirectories),
  };
};

type VersionUploadContext = CommonUploadContext & {
  assetId: string;
  basePath?: string;
  copy?: {
    sourceVersionId: string;
    copyMetadata: boolean;
    copySegments: CreateVersionCopyableSegment[];
  };
};

export const toVersionUpload = (
  file: PickedFile,
  context: VersionUploadContext,
): UploadRequest => {
  const version: CreateVersionParams = context.copy
    ? { assetId: context.assetId, ...context.copy }
    : { assetId: context.assetId };
  return {
    fileInfo: toFileInfo(file, file.name),
    options: {
      ...toCommonOptions(context),
      basePath: context.basePath,
    },
    target: { type: 'newVersion', ...version },
    uploadKind: { kind: 'file' },
  };
};

type ExistingAssetUploadContext = CommonUploadContext & {
  assetId: string;
  /** Where the asset's files already are on the storage. */
  basePath: string;
};

export const toCustomFormatUpload = (
  file: PickedFile,
  context: ExistingAssetUploadContext & { formatName: string },
): UploadRequest => ({
  fileInfo: toFileInfo(file, file.name),
  options: {
    ...toCommonOptions(context),
    basePath: context.basePath,
  },
  target: { type: 'existingAsset', assetId: context.assetId },
  uploadKind: {
    kind: 'file',
    formatName: context.formatName,
  },
});

export const toSubtitleUpload = (
  file: PickedFile,
  context: ExistingAssetUploadContext & {
    formatName: string;
    metadata?: NewSubtitleMetadata;
    createTranscription: boolean;
    deleteOldTranscriptions?: boolean;
  },
): UploadRequest => {
  const formatMetadata = Object.fromEntries(
    Object.entries(context.metadata ?? {}).map(([key, value]) => [
      key,
      String(value),
    ]),
  );
  return {
    fileInfo: toFileInfo(file, file.name),
    options: {
      ...toCommonOptions(context),
      basePath: context.basePath,
    },
    target: { type: 'existingAsset', assetId: context.assetId },
    uploadKind: {
      kind: 'file',
      formatName: context.formatName,
      formatMetadata: [formatMetadata],
      slugifyFileName: true,
      subtitles: {
        createTranscription: context.createTranscription,
        deleteOldTranscriptions: context.deleteOldTranscriptions,
      },
    },
  };
};

export const toPlaceholderUpload = (
  file: PickedFile,
  context: CommonUploadContext & { assetId: string; basePath?: string },
): UploadRequest => ({
  fileInfo: toFileInfo(file, file.name),
  options: {
    ...toCommonOptions(context),
    basePath: context.basePath,
  },
  target: {
    type: 'existingAsset',
    assetId: context.assetId,
    fillsPlaceholder: true,
  },
  uploadKind: { kind: 'file' },
});

export const toProxyUpload = (
  file: PickedFile,
  context: CommonUploadContext & {
    assetId: string;
    proxy: FilesProxyCreateSchema;
  },
): UploadRequest => ({
  fileInfo: toFileInfo(file, file.name),
  options: toCommonOptions(context),
  target: { type: 'existingAsset', assetId: context.assetId },
  uploadKind: { kind: 'proxy', create: context.proxy },
});
